import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { MarkdownEditor } from '../components/Editor/MarkdownEditor';
import { docsApi, Document } from '../services/api';
import { useDocsStore } from '../store/docsStore';
import '../styles/page.css';

export const EditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const { documents, isOnline, fetchDocuments, fetchDocumentBySlug } = useDocsStore();

  // Redirect if offline
  useEffect(() => {
    if (!isOnline) {
      navigate('/');
    }
  }, [isOnline, navigate]);

  useEffect(() => {
    if (id && isOnline) {
      loadDocument();
    }
  }, [id, isOnline]);

  const loadDocument = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      // Ensure we have documents loaded
      if (!documents) {
        await fetchDocuments();
      }
      
      // Find document by ID
      const docsToSearch = documents || [];
      const doc = docsToSearch.find(d => d.id === parseInt(id));
      
      if (doc) {
        // Load full document with content
        await fetchDocumentBySlug(doc.slug);
        const fullDoc = await docsApi.getBySlug(doc.slug);
        setDocument(fullDoc.data);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout showSidebar={false}>
        <p>Loading...</p>
      </Layout>
    );
  }

  if (!document) {
    return null;
  }

  return (
    <Layout showSidebar={false}>
      <div className="page-header">
        <h1 className="page-title">Edit Document</h1>
        <p className="page-subtitle">Update your documentation content</p>
      </div>
      <MarkdownEditor
        mode="edit"
        documentId={document.id}
        initialTitle={document.title}
        initialContent={document.content}
        initialFolderPath={document.folder_path}
        initialIsPrivate={document.is_private}
        initialTags={document.tags}
      />
    </Layout>
  );
};
